import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Spinner } from './ui/spinner';
import { storyApi } from '../services/api';

interface ImportPasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: (password: string) => void;
}

/**
 * Asks for the import password and checks it against the API
 * before the Publish-Reorder flow is opened.
 */
export function ImportPasswordDialog({ isOpen, onClose, onVerified }: ImportPasswordDialogProps) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetAndClose = () => {
    setPassword('');
    setError('');
    onClose();
  };

  const handleClose = () => {
    if (!loading) resetAndClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || loading) return;
    setLoading(true);
    setError('');
    try {
      const response = await storyApi.verifyImportPassword(password);
      if (response.data.data === true) {
        setPassword('');
        onVerified(password);
        return;
      }
      setError('Something went wrong. Unauthorized');
    } catch {
      setError('Something went wrong. Unauthorized');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent onClose={handleClose} className="max-w-sm">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Enter Import Password</DialogTitle>
            <DialogDescription>A password is required to publish and reorder stories.</DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            autoFocus
            placeholder="Password"
            value={password}
            disabled={loading}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
          />
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" size="sm" disabled={!password || loading}>
              {loading && <Spinner size="sm" className="mr-2" />}
              Proceed
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
